import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  Tag,
  X,
  CalendarDays,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import Hero from "../components/Hero";
import debounce from "lodash/debounce";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";

const AnalysesOverview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlSearchTerm = searchParams.get("search") || "";
  const urlHoofdcategorie = searchParams.get("hoofdcategorie") || "";

  // State
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const [filters, setFilters] = useState({
    documentType: "",
    hoofdcategorieen: urlHoofdcategorie ? [urlHoofdcategorie] : [],
    company: "",
  });
  const [sortConfig, setSortConfig] = useState({
    field: "date",
    direction: "desc",
  });
  const [uniqueCategories, setUniqueCategories] = useState(new Set());
  const [uniqueCompanies, setUniqueCompanies] = useState(new Set());

  // Main data fetching function
  const fetchData = async (pageNum = page, shouldResetPage = false) => {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: "20",
        search: searchTerm,
        documentType: filters.documentType,
        hoofdcategorie: filters.hoofdcategorieen.join(","),
        company: filters.company,
        sortDirection: sortConfig.direction,
      });

      console.log("Fetching data with params:", params.toString());

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/analyses-paginated?${params}`
      );
      const { data, meta } = await response.json();

      setAnalyses(data);
      setTotalPages(meta.totalPages || 1);
      setTotalResults(meta.total);

      if (shouldResetPage) {
        setPage(1);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch analyses");
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        navigate(`/?search=${encodeURIComponent(value)}`, { replace: true });
        fetchData(1, true);
      }, 500),
    [navigate, filters, sortConfig.direction] // Missing fetchData dependency
  );
  // Initial fetch and metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        // Fetch categories and companies in parallel
        const [categoriesRes, companiesRes, countRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/unique-categories`),
          fetch(`${process.env.REACT_APP_API_URL}/unique-companies`),
          fetch(`${process.env.REACT_APP_API_URL}/analyses-count`),
        ]);

        const categories = await categoriesRes.json();
        const companies = await companiesRes.json();
        const { count } = await countRes.json();

        setUniqueCategories(new Set(categories));
        setUniqueCompanies(new Set(companies));
        setTotalCount(count);
      } catch (error) {
        console.error("Error fetching metadata:", error);
        setError("Failed to fetch filters");
      }
    };

    fetchMetadata();
    fetchData(1); // Initial data fetch
  }, []);

  // Handle filter changes
  useEffect(() => {
    if (!loading) {
      fetchData(1, true);
    }
  }, [filters, sortConfig.direction]);

  const handlePageChange = async (newPage) => {
    if (loading || newPage < 1 || newPage > totalPages) {
      return;
    }

    setPage(newPage);
    setLoading(true);

    try {
      const params = new URLSearchParams({
        page: newPage.toString(),
        limit: "20",
        search: searchTerm,
        documentType: filters.documentType,
        hoofdcategorie: filters.hoofdcategorieen.join(","),
        company: filters.company,
        sortDirection: sortConfig.direction,
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/analyses-paginated?${params}`
      );
      const { data, meta } = await response.json();

      setAnalyses(data);
      setTotalPages(meta.totalPages || 1);
      setTotalResults(meta.total);

      // Only scroll after data is loaded
      document
        .getElementById("uitspraken")
        .scrollIntoView({ behavior: "smooth" });
    } catch (error) {
      console.error("Error fetching page data:", error);
      setError("Failed to fetch analyses");
    } finally {
      setLoading(false);
    }
  };
  // Filter handlers
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleDocumentTypeChange = (newValue) => {
    console.log("Document type changed:", newValue);
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        documentType: newValue,
      };
      console.log("New filters:", newFilters);
      return newFilters;
    });
  };
  const handleCompanyChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      company: value,
    }));
  };

  const handleCategoryChange = (category) => {
    setFilters((prev) => ({
      ...prev,
      hoofdcategorieen: prev.hoofdcategorieen.includes(category)
        ? prev.hoofdcategorieen.filter((c) => c !== category)
        : [...prev.hoofdcategorieen, category],
    }));
  };

  const clearFilters = () => {
    setFilters({
      documentType: "",
      hoofdcategorieen: [],
      company: "",
    });
    setSearchTerm("");
    navigate("/");
    fetchData(1, true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const [day, month, year] = dateString
        .split("-")
        .map((num) => parseInt(num, 10));
      const date = new Date(year, month - 1, day);

      if (isNaN(date.getTime())) return "-";

      return date.toLocaleDateString("nl-NL", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Date formatting error:", error);
      return "-";
    }
  };

  // Initial loading state
  if (loading && analyses.length === 0) {
    return (
      <div className="flex items-center justify-center bg-nn-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nn-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-nn-gray-50">
      <div className="w-full">
        <Hero />

        {/* Search Section */}
        <div id="zoeken" className="mt-32 mb-12">
          {/* Search header */}
          <div className="flex flex-col space-y-4 lg:space-y-0 lg:flex-row lg:justify-between lg:items-center mb-6">
            {/* Title and description section */}
            <div className="max-w-2xl space-y-3">
              <h1 className="text-xl lg:text-2xl font-bold text-nn-500">
                Zoek uitspraken & adviezen
              </h1>
              <p className="text-sm lg:text-base text-nn-gray-600">
                Filter CGR-beslissingen op type uitspraak, onderwerp of
                betrokken bedrijven. Elke uitspraak is voorzien van een
                AI-samenvatting en praktische compliance lessen.
              </p>
            </div>

            {/* Filter status - Moved to top on mobile */}
            <div className="flex flex-col-reverse lg:flex-row items-start lg:items-center gap-2 lg:gap-4">
              {(filters.documentType ||
                filters.hoofdcategorieen.length > 0 ||
                filters.company) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-nn-500 hover:text-nn-600 text-sm"
                >
                  <X className="h-4 w-4" />
                  Wis filters
                </button>
              )}
              <span className="inline-flex text-sm bg-nn-50 text-nn-600 px-3 py-1 rounded-full">
                {totalResults} van {totalCount} uitspraken
              </span>
            </div>
          </div>

          {/* Search input */}
          <div className="w-full mb-6">
            <input
              type="text"
              placeholder="🔍 Zoek op zaaknummer, tekst..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg
               placeholder:text-gray-400 text-gray-900
               focus:outline-none focus:ring-1 focus:ring-gray-200"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>

          {/* Filters - Main container with responsive stack */}
          <div className="flex flex-col space-y-6 mb-6">
            {/* Document Type Toggle */}
            <div className="w-full">
              <label className="block text-sm font-medium text-nn-gray-600 mb-2">
                Filter op type uitspraak
              </label>
              <ToggleGroup
                type="single"
                value={filters.documentType || ""}
                onValueChange={handleDocumentTypeChange}
                className="flex flex-wrap gap-2 justify-start"
              >
                {["klacht", "advies"].map((type) => (
                  <ToggleGroupItem
                    key={type}
                    value={type}
                    className="
            px-4 py-2 
            capitalize 
            text-left
            border border-nn-gray-200
            text-nn-gray-700
            bg-white
            hover:bg-nn-gray-50
            hover:border-nn-gray-300
            rounded-lg
            transition-all
            data-[state=on]:bg-nn-500 
            data-[state=on]:text-white
            data-[state=on]:border-nn-500
            data-[state=on]:hover:bg-nn-600
            data-[state=on]:hover:border-nn-600
            font-medium
          "
                  >
                    {type}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>

            {/* Company Select */}
            <div className="w-full">
              <label className="block text-sm font-medium text-nn-gray-600 mb-2">
                Filter op bedrijf
              </label>
              <Select
                value={filters.company}
                onValueChange={handleCompanyChange}
              >
                <SelectTrigger className="w-full">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-nn-gray-400" />
                    <SelectValue placeholder="Alle bedrijven" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <ScrollArea className="h-[200px]">
                    <SelectItem value="all">Alle bedrijven</SelectItem>
                    {[...uniqueCompanies].map((company) => (
                      <SelectItem key={company} value={company}>
                        {company}
                      </SelectItem>
                    ))}
                  </ScrollArea>
                </SelectContent>
              </Select>
            </div>

            {/* Categories */}
            <div className="w-full">
              <label className="block text-sm font-medium text-nn-gray-600 mb-2">
                Filter op onderwerp
              </label>
              <div className="flex flex-wrap gap-2">
                {[...uniqueCategories].map((category) => (
                  <Badge
                    key={category}
                    variant="outline"
                    className={`cursor-pointer transition-colors ${
                      filters.hoofdcategorieen.includes(category)
                        ? "bg-nn-500 text-white hover:bg-nn-600"
                        : "hover:bg-nn-50"
                    }`}
                    onClick={() => handleCategoryChange(category)}
                  >
                    <Tag
                      className={`h-3 w-3 mr-1 ${
                        filters.hoofdcategorieen.includes(category)
                          ? "text-white"
                          : "text-nn-gray-400"
                      }`}
                    />
                    {category}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div>
          <div
            id="uitspraken"
            className="flex justify-between items-center mb-6"
          >
            <div className="max-w-2xl space-y-4">
              <h2 className="text-2xl font-bold text-nn-500">Uitspraken</h2>
              <p>
                Gevonden uitspraken gesorteerd op datum. Filter verder of klik
                op een uitspraak voor details en praktische lessen.
              </p>
            </div>

            {/* Sort button */}
            <button
              onClick={() =>
                setSortConfig((prev) => ({
                  ...prev,
                  direction: prev.direction === "asc" ? "desc" : "asc",
                }))
              }
              className="text-sm bg-nn-50 text-nn-600 px-3 py-1 rounded-full hover:bg-nn-100 transition-colors inline-flex items-center gap-1"
            >
              <CalendarDays className="h-4 w-4" />
              <span>Datum</span>
              {sortConfig.direction === "asc" ? (
                <ArrowUp className="h-4 w-4" />
              ) : (
                <ArrowDown className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Results grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {/* Loading indicator */}
            {loading && (
              <div className="col-span-full flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nn-500"></div>
              </div>
            )}

            {/* Analysis cards */}
            {!loading &&
              analyses.map((analysis) => {
                const basisInfo = analysis.json_output?.basis_informatie;
                const isKlacht = analysis.document_type === "klacht";

                return (
                  <div
                    key={analysis.id}
                    className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-all duration-200 cursor-pointer border border-transparent hover:border-nn-200"
                    onClick={() => navigate(`/analyses/${analysis.id}`)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span
                        className={`p-2 rounded-md text-sm font-medium ${
                          isKlacht
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {isKlacht ? "Klacht " : "Advies"}
                      </span>
                      <div className="flex items-center text-nn-gray-500 text-sm">
                        <CalendarDays className="h-5 w-10 mr-2" />
                        <span>{formatDate(basisInfo?.datum_uitspraak)}</span>
                      </div>
                    </div>

                    <div className="text-nn-gray-500 text-sm mb-2">
                      {basisInfo?.zaaknummer}
                    </div>

                    <h2 className="text-lg font-semibold text-nn-500 mb-4">
                      {basisInfo?.krantenkop}
                    </h2>

                    {analysis.json_output?.metadata?.hoofdcategorie && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="inline-flex items-center p-2 bg-nn-gray-100 text-nn-gray-700 rounded-md text-sm">
                          <Tag className="h-4 w-8 mr-2 text-nn-gray-500" />
                          {analysis.json_output.metadata.hoofdcategorie}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

            {/* No results state */}
            {!loading && analyses.length === 0 && (
              <div className="col-span-full bg-white rounded-lg shadow-sm p-6 text-center">
                <p className="text-nn-gray-500">Geen resultaten gevonden</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && analyses.length > 0 && totalPages > 1 && (
            <div className="flex justify-center mt-8 mb-12">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => handlePageChange(page - 1)}
                      className={`cursor-pointer ${
                        page === 1 ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      disabled={page === 1 || loading}
                    />
                  </PaginationItem>

                  {/* First page */}
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => handlePageChange(1)}
                      isActive={page === 1}
                    >
                      1
                    </PaginationLink>
                  </PaginationItem>

                  {/* Show ellipsis if there are many pages */}
                  {page > 3 && <PaginationEllipsis />}

                  {/* Current page neighborhood */}
                  {page > 2 && (
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => handlePageChange(page - 1)}
                      >
                        {page - 1}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  {page > 1 && page < totalPages && (
                    <PaginationItem>
                      <PaginationLink isActive>{page}</PaginationLink>
                    </PaginationItem>
                  )}

                  {page < totalPages - 1 && (
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => handlePageChange(page + 1)}
                      >
                        {page + 1}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  {/* Show ellipsis if there are many pages */}
                  {page < totalPages - 2 && <PaginationEllipsis />}

                  {/* Last page */}
                  {totalPages > 1 && (
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => handlePageChange(totalPages)}
                        isActive={page === totalPages}
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => handlePageChange(page + 1)}
                      className={`cursor-pointer ${
                        page === totalPages
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                      disabled={page === totalPages || loading}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysesOverview;
